import React, { useState } from 'react';
import compose from 'helpers/compose';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FormikProps } from 'formik';

import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { CircularProgress } from '@material-ui/core';

import FileUpload from 'components/FormFields/FileUpload';

import { EdwardColors } from 'enums';
import { API_FUNCTION_TYPE } from 'api/endpoints';
import { RootStore } from 'stores/rootStore';
import { Method } from 'stores/apiStore';

interface Props {
  accountId: string;
  docTitle?: string;
  docName: string;
  beFile?: string;
  endpoint: API_FUNCTION_TYPE;
  rootStore?: RootStore;
  formikProps: FormikProps<any>;
  disabled?: boolean;
}

const RequiredDocUpload: React.FC<Props> = ({
  accountId,
  docTitle,
  docName,
  beFile,
  endpoint,
  rootStore,
  formikProps,
  disabled,
}) => {
  const { t } = useTranslation();

  const [uploadFile, setUploadFile] = useState<null | File>(null);
  const [uploadedFile, setUploadedFile] = useState<null | boolean>(null);

  const onUploadChange = (e: any) => {
    setUploadFile(e.target.files[0]);
  };

  const upload = async () => {
    const formData = new FormData();
    formData.append('filename', uploadFile!);

    setUploadedFile(false);
    const response = await rootStore!.stores.apiStore.handleRequest<File>(endpoint(accountId), Method.POST, formData, {
      method: Method.POST,
      body: formData,
      headers: {
        Authorization: `Bearer ${rootStore!.stores.apiStore.authToken}`,
        // No Content-Type: https://stackoverflow.com/a/49510941
      },
    });
    if (response?.errors) {
      response.errors.forEach((error) => rootStore!.stores.errorStore.addGlobalError(error.message || error.key));
      setUploadedFile(null);
    } else {
      setUploadedFile(true);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <strong>{docTitle ? t(docTitle) : ''}</strong>
      </Grid>

      <Grid item xs={3} style={{ overflow: 'hidden ' }}>
        <FileUpload
          name={docName}
          accept=".jpg,.png,.pdf"
          onChange={(e: any) => onUploadChange(e)}
          upload={upload}
          specificError
          errors={uploadedFile === true ? undefined : formikProps.errors}
          vertical
          disabled={disabled}
          accepted={t('contract_aml_file_info')}
        />
      </Grid>
      <Grid item xs={9} style={{ lineHeight: '36px' }}>
        {uploadedFile === null && !beFile && t('contract_aml_file_info')}
        {(uploadedFile === true || beFile) && (
          <span style={{ color: EdwardColors.GREEN, fontSize: '19px' }}>
            <CheckCircleIcon style={{ verticalAlign: 'text-bottom', marginRight: '8px' }} />
            {t('general_uploaded')}
            {(uploadFile?.name || beFile) && <span style={{ color: '#000' }}>&nbsp;({uploadFile?.name || beFile})</span>}
          </span>
        )}
        {uploadedFile === false && (
          <span style={{ color: EdwardColors.SECONDARY, fontSize: '19px' }}>
            <CircularProgress
              color={'secondary'}
              size={20}
              style={{ verticalAlign: 'text-bottom', marginRight: '8px' }}
            />
            {t('general_uploading')}
          </span>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} />
    </>
  );
};

export default compose(inject('rootStore'), observer)(RequiredDocUpload);
